<template>
  <tbody>
    <template v-if="displayRows.length > 0">
      <template v-for="(row, key) of displayRows" :key="row.id">
        <tr
          :class="getClassTr(getGlobalIndex(key), row)"
          :style="getStyleTr(row, getGlobalIndex(key))"
          @click="() => onActive(getGlobalIndex(key))"
          @dblclick="() => $emit('select', row)"
          @contextmenu.prevent="
            (event) => onOpenMenu(event, row, getGlobalIndex(key))
          "
          :ref="
            (el) => {
              if (el) rows[getGlobalIndex(key)] = el;
            }
          "
          tabindex="-1"
        >
          <th v-if="config.menu" style="width: 20px; text-align: center">
            <a
              href="#"
              @click.prevent="
                (event) => onOpenMenu(event, row, getGlobalIndex(key))
              "
            >
              <i class="icon icon-menu"></i>
            </a>
          </th>

          <th v-if="config.rowSelect" style="width: 25px; text-align: center">
            <input
              type="checkbox"
              class="row-checkbox"
              :checked="store.data.select.includes(row.id)"
              @click="(e) => selectRow(e, row)"
            />
          </th>

          <th v-if="config.numbering" style="width: 40px; text-align: center">
            {{ getNumbering(key) }}
          </th>

          <template v-for="fieldkey of sortedFields" :key="fieldkey">
            <template v-if="visible(getField(fieldkey))">
              <template v-if="getField(fieldkey).config.width">
                <td
                  :style="
                    getStyleTd(getField(fieldkey), row, getGlobalIndex(key))
                  "
                >
                  <div
                    :class="{ cutoff: !!getField(fieldkey).config.cutoff }"
                    :style="{ width: getField(fieldkey).config.width }"
                    v-html="getData(getField(fieldkey), fieldkey, row)"
                  ></div>
                </td>
              </template>
              <template v-else-if="!!config.cellClick[fieldkey]">
                <td
                  :style="
                    getStyleTd(getField(fieldkey), row, getGlobalIndex(key))
                  "
                >
                  <a
                    v-html="getData(getField(fieldkey), fieldkey, row)"
                    href="#"
                    @click.prevent="
                      () => config.cellClick[fieldkey](row, controller)
                    "
                  ></a>
                </td>
              </template>
              <template v-else>
                <td
                  v-html="getData(getField(fieldkey), fieldkey, row)"
                  :style="
                    getStyleTd(getField(fieldkey), row, getGlobalIndex(key))
                  "
                ></td>
              </template>
            </template>
          </template>
        </tr>
      </template>
    </template>
    <template v-else>
      <tr>
        <td
          :colspan="
            Object.keys(store.state?.fields || {}).length +
            (config.menu ? 1 : 0) +
            (config.numbering ? 1 : 0)
          "
        >
          &nbsp;
        </td>
      </tr>
    </template>
  </tbody>
</template>

<script lang="tsx">
import dayjs from "dayjs";
import { defineComponent, computed } from "vue";
import openContextMenu from "@/components/openContextMenu";
import { asDateOnlyString, formatTimeHm } from "@/helpers/dateOnly";

export default defineComponent({
  emits: ["active", "select"],

  props: {
    controller: {
      type: Object,
      default: () => ({}),
    },
    crossot: {
      type: Function,
      default: () => false,
    },
  },

  setup(props: any, { emit }: { emit: any }) {
    const store = props.controller.store;

    const config = props.controller.settings;

    const rows: any = props.controller.rows;

    const isLocalPaginatedSubtable = computed(() => {
      return !!(config.allowSubtablePagination && store?.model?.subtable);
    });

    const filteredRows = computed(() => {
      const allRows = store.data.rows || [];
      if (typeof config.rowFilter === "function") {
        return allRows.filter((row: any) => !!config.rowFilter(row));
      }
      return allRows;
    });

    const displayRows = computed(() => {
      const allRows = filteredRows.value;

      if (!isLocalPaginatedSubtable.value) {
        return allRows;
      }

      const page = props.controller.page?.value || 0;
      const limit = props.controller.limit?.value || allRows.length;
      const start = page * limit;

      return allRows.slice(start, start + limit);
    });

    const getGlobalIndex = (localIndex: number | string) => {
      const index =
        typeof localIndex === "string" ? parseInt(localIndex, 10) : localIndex;

      const displayed = displayRows.value;
      const row = displayed[index];
      if (row && store.data.rows) {
        const realIdx = store.data.rows.indexOf(row);
        if (realIdx >= 0) return realIdx;
      }

      if (!isLocalPaginatedSubtable.value) {
        return index;
      }

      const page = props.controller.page?.value || 0;
      const limit = props.controller.limit?.value || 0;

      return page * limit + index;
    };

    const getData = (structure: any, field: any, data: any) => {
      const calc = structure?.config?.calc;

      if (calc) return typeof calc == "function" ? calc(data) : calc;

      let value = data[field];

      if (typeof structure?.config?.render === "function") {
        const rendered = structure.config.render(data[field], data);
        if (rendered !== undefined) {
          return rendered;
        }
      }

      if (data[`_${field}`]) {
        value = data[`_${field}`];
      } else {
        if (typeof structure.type == "object") {
          if (structure.type.table) {
            value = value ? `Objects [${value.length}]` : null;
          } else if (structure.type.fields) {
            const field_structure = store.model?.fields?.[data[field]];

            value = field_structure
              ? getData(field_structure, data[field], data)
              : null;
          }
        } else {
          switch (structure.type) {
            case "DECIMAL":
              if (structure?.config?.precision) {
                value = Number(value && !isNaN(value) ? value : 0).toFixed(
                  structure.config.precision,
                );
              } else {
                value = Number(value && !isNaN(value) ? value : 0);
              }

              break;
            case "DATEONLY":
              if (data[field] && data[field] != "0000-00-00") {
                const ymd = asDateOnlyString(data[field]);
                if (ymd) value = dayjs(ymd).format("DD.MM.YYYY");
              }

              break;
            case "TIME":
              if (data[field]) value = formatTimeHm(data[field]);

              break;
            case "DATE":
              if (data[field])
                value = dayjs(data[field]).format("DD.MM.YYYY HH:mm:ss");

              break;
            case "BOOLEAN":
              value = data[field] ? '<i class="icon icon-check"></i>' : "";

              break;
          }
        }
      }

      return value;
    };

    const getStyleTr = (data: any, rowIndex: number) => {
      const result: any = {};

      if (config.styleTr) {
        if (typeof config.styleTr == "function") {
          Object.assign(result, config.styleTr(data));
        } else if (typeof config.styleTr == "object") {
          Object.assign(result, config.styleTr);
        }
      }

      // Если строка активна (выделена), применяем цвет выделения поверх других стилей
      if (rowIndex === store.data.position) {
        result["background-color"] = "#e0f7fa";
      }

      return result;
    };

    const getStyleTd = (structure: any, data: any, rowIndex: number) => {
      const result: any = {};

      if (structure?.config.style) {
        if (typeof structure.config.style == "function") {
          Object.assign(result, structure.config.style(data));
        } else if (typeof structure.config.style == "object") {
          Object.assign(result, structure.config.style);
        }
      }

      if (structure?.config?.sticky) {
        result["position"] = "sticky";
        result["left"] = "0";
        result["z-index"] = "9";

        if (rowIndex === store.data.position) {
          result["background-color"] = "#f3e4e7";
        } else {
          const trStyle = getStyleTr(data, rowIndex);
          if (trStyle && trStyle["background-color"]) {
            result["background-color"] = trStyle["background-color"];
          } else {
            result["background-color"] =
              rowIndex % 2 === 0 ? "#f8fafc" : "#ffffff";
          }
        }
      }

      return result;
    };

    const getNumbering = (numberRow: number | string) => {
      const index =
        typeof numberRow === "string" ? parseInt(numberRow, 10) : numberRow;
      return store.fetchOptions.page * store.fetchOptions.limit + index + 1;
    };

    const onActive = (position: number) => emit("active", position);

    const getClassTr = (key: number, data: any) => {
      const classes = {
        selectrow: key == store.data.position,
        "line-through": props.crossot(data),
      };

      if (config.classTr) {
        if (typeof config.classTr == "function") {
          Object.assign(classes, config.classTr(data));
        } else if (typeof config.classTr == "object") {
          Object.assign(classes, config.classTr);
        }
      }

      return classes;
    };

    const onOpenMenu = (event: any, data: any, position: number) => {
      emit("active", position);

      config.menu &&
        openContextMenu(event, props.controller.contextMenuBody(data));
    };

    const selectRow = (e: any, row: any) => {
      config.onRowSelect(row, e.target.checked);

      e.target.checked = false;
    };

    const visible = (field: any) => {
      const config = field?.config;

      return (
        (typeof config.visible == "function"
          ? config.visible()
          : config.visible) && !config.hide
      );
    };

    const getField = (fieldKey: string) => store.state.fields[fieldKey];

    const sortedFields = props.controller.sortedFields;

    return {
      config,
      store,
      rows,
      displayRows,
      getGlobalIndex,
      onActive,
      onOpenMenu,
      getData,
      getNumbering,
      getClassTr,
      getStyleTd,
      getStyleTr,
      selectRow,
      visible,
      getField,
      sortedFields,
      controller: props.controller,
    };
  },
});
</script>

<style scoped>
.row-checkbox {
  position: relative;
  left: 0px;
  opacity: 1;
  top: 3px;
}

.line-through {
  text-decoration: line-through;
}
</style>
